import React from 'react';

import { cn } from '@/lib/utils';

import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../components/ui/table';
import { AssistantTable } from '../../../../common/hooks/use-assistant-api.hook';
import { RichText } from './RichText';

/**
 * A comparison, drawn rather than described.
 *
 * "How do we compare to this competitor" is eight figures in pairs, and as a
 * paragraph the vendor has to hold each pair in their head to see which way it
 * goes. The grid does that for them.
 *
 * The panel is narrow — 380px docked — so the table scrolls sideways inside
 * its own box rather than widening the conversation. Cells go through RichText
 * for the same reason the messages do: a colour in a comparison should be a
 * swatch here too.
 */

interface Props {
    table: AssistantTable;
}

export const ReplyTable: React.FC<Props> = ({ table }) => {
    if (!table?.columns?.length || !table?.rows?.length) return null;

    return (
        <div className="mt-2.5 overflow-hidden rounded-xl border">
            {table.title && (
                <p className="border-b px-3 py-2 text-xs font-medium text-muted-foreground">{table.title}</p>
            )}

            {/*
              * The scroll lives here, around the table only. On the page body
              * it would let a wide comparison push the whole conversation
              * sideways.
              */}
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {table.columns.map((column, i) => (
                                <TableHead
                                    key={`${column}-${i}`}
                                    // Numbers read right-aligned; the first
                                    // column is what each row IS, so it stays
                                    // left however it is written.
                                    className={i === 0 ? undefined : 'text-right'}
                                >
                                    {column}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {table.rows.map((row, r) => (
                            <TableRow key={r}>
                                {row.map((cell, c) => (
                                    <TableCell
                                        key={c}
                                        className={cn(c === 0 ? 'font-medium' : 'text-right', 'whitespace-nowrap')}
                                    >
                                        <RichText text={cell} />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                    {table.note && <TableCaption className="px-3 pb-2 text-left">{table.note}</TableCaption>}
                </Table>
            </div>
        </div>
    );
};
